import { useEffect, useMemo, useState } from "react";
import { api } from "../data/api";
import { Button, Card } from "../components/ui";
import { useAuth } from "../auth/AuthContext";

/// AutoMessagesPage — admin/coordinator gestiona las plantillas de
/// mensajes automáticos que el sistema dispara. Por ahora solo soporta
/// WhatsApp al vendedor cuando hay rechazo; cuando agreguemos otros
/// tipos (email al supervisor, SMS al cliente), aparecen como secciones
/// adicionales acá.
///
/// El chofer en mobile lee `enabled` + `body_template` al login. Si
/// está deshabilitado, no aparece el diálogo de WhatsApp tras rechazo.
export function AutoMessagesPage() {
  const { currentUser } = useAuth();
  if (currentUser?.role !== "admin" && currentUser?.role !== "coordinator") {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card padding="lg" className="max-w-md text-center">
          <h2 className="text-base font-semibold text-ink mb-2">
            Acceso restringido
          </h2>
          <p className="text-sm text-sub">
            Solo administradores y coordinadores pueden configurar mensajes.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-ink">Mensajes automáticos</h1>
        <p className="text-xs text-sub mt-0.5">
          Configurá los mensajes que el sistema dispara en distintos eventos.
        </p>
      </header>

      <WhatsAppRejectSection />
    </div>
  );
}

// Variables que el chofer puede usar en el template. Se renderean en
// el mobile al momento de armar el mensaje (no en backend). El orden
// refleja el caso de uso típico (saludo → contexto → cierre).
const VARIABLES = [
  { key: "{vendedor}", desc: "Nombre del vendedor" },
  { key: "{cliente}", desc: "Nombre del cliente" },
  { key: "{orden}", desc: "Código de la orden" },
  { key: "{motivo}", desc: "Razón del rechazo" },
  { key: "{notas}", desc: "Notas que el chofer escribió" },
  { key: "{direccion}", desc: "Dirección del stop" },
  { key: "{chofer}", desc: "Nombre del chofer" },
  { key: "{fecha}", desc: "Fecha y hora del rechazo" },
];

function WhatsAppRejectSection() {
  const [enabled, setEnabled] = useState(false);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  // Estado inicial para detectar "dirty" — deshabilita Guardar si no
  // hubo cambios.
  const [initial, setInitial] = useState<{ enabled: boolean; body: string }>({
    enabled: false,
    body: "",
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getNotificationTemplate("whatsapp_reject")
      .then((t) => {
        if (cancelled) return;
        setEnabled(t.enabled);
        setBody(t.body_template);
        setInitial({ enabled: t.enabled, body: t.body_template });
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(
    () => enabled !== initial.enabled || body !== initial.body,
    [enabled, body, initial],
  );

  // Preview con variables sustituidas por valores de ejemplo, así el
  // admin ve cómo le va a llegar al vendedor antes de guardar.
  const preview = useMemo(() => {
    return body
      .replaceAll("{vendedor}", "Carlos Pérez")
      .replaceAll("{cliente}", "Bodega La Esquina")
      .replaceAll("{orden}", "ORD-12345")
      .replaceAll("{motivo}", "Cliente ausente")
      .replaceAll("{notas}", "Toqué timbre 3 veces, sin respuesta")
      .replaceAll("{direccion}", "Av. Abancay 532, Cercado de Lima")
      .replaceAll("{chofer}", "Juan García")
      .replaceAll("{fecha}", "28/06/2026 14:30");
  }, [body]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      await api.upsertNotificationTemplate("whatsapp_reject", {
        enabled,
        body_template: body,
      });
      setInitial({ enabled, body });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function insertVariable(varKey: string) {
    setBody((prev) => prev + varKey);
  }

  if (loading) {
    return (
      <Card padding="md">
        <p className="text-sm text-sub">Cargando…</p>
      </Card>
    );
  }

  return (
    <Card padding="md" className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-ink">
          WhatsApp al vendedor (al rechazar)
        </h2>
        <p className="text-xs text-sub mt-0.5">
          Cuando el chofer rechaza un pedido desde mobile, el sistema le ofrece
          enviar un WhatsApp al vendedor (si el pedido tiene su teléfono
          cargado). El chofer abre el chat ya con el mensaje pre-armado y solo
          aprieta Enviar — el costo lo paga el chip del chofer, no el sistema.
        </p>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="w-4 h-4"
        />
        <span className="text-sm text-ink">
          {enabled ? "Activado" : "Desactivado"}
        </span>
        <span className="text-xs text-sub">
          {enabled
            ? " — los choferes verán el diálogo de WhatsApp tras cada rechazo"
            : " — los choferes no verán el diálogo"}
        </span>
      </label>

      <div>
        <label className="text-xs uppercase tracking-wide text-sub block mb-1">
          Plantilla del mensaje
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full border border-border rounded p-2 text-sm font-mono"
          placeholder="Hola {vendedor}, el pedido {orden} para {cliente} fue rechazado..."
        />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-sub mb-1">
          Variables disponibles
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VARIABLES.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => insertVariable(v.key)}
              title={`${v.desc} — click para insertar`}
              className="px-2 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary text-xs font-mono hover:bg-primary/20"
            >
              {v.key}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-sub mt-1">
          Click una variable para insertarla al final. Las variables no
          reconocidas se dejan literales en el mensaje.
        </p>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-sub mb-1">
          Preview (con datos de ejemplo)
        </div>
        <div className="border border-border rounded p-3 bg-page/30 whitespace-pre-wrap text-sm">
          {preview || (
            <span className="text-sub italic">Escribí un mensaje arriba…</span>
          )}
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 bg-state-failed/10 border border-state-failed/30 text-xs text-state-failed rounded">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? "Guardando…" : "Guardar"}
        </Button>
        {savedAt && (
          <span className="text-xs text-sub">Guardado a las {savedAt}</span>
        )}
        {dirty && !savedAt && (
          <span className="text-xs text-accent">Cambios sin guardar</span>
        )}
      </div>
    </Card>
  );
}
